// İSG Portal — Belge durum sunumu (araç ve personel belgeleri ortak). Sunucunun `validityStatus`/`approvalStatus`
// değerini UI'a çeviren TEK yer (istemci yeniden hesaplamaz; §23.0.3). SP ve EP AYNI eşlemeyi
// kullanır; etiketler `compliance.*` / `permits.docApproval.*` anahtarlarından, her portalın kendi
// metniyle gelir.

import type { TFunction } from 'i18next';
import type { ComplianceStatus } from '../api/contractors';
import type { DocumentStatusTag } from '../ui/DocumentRow';
import type { TagColor } from '../ui/Tag';

/** Araç ve personel belge DTO'larının ortak alanları; yardımcı bunun dışında hiçbir şey okumaz. */
export interface DocumentFacts {
  validFrom: string | null;
  validUntil: string | null;
  approvalStatus: 'Pending' | 'Approved' | 'Rejected';
  validityStatus: ComplianceStatus;
}

/**
 * Tarih satırı, dört durum: iki tarih de varsa aralık (`başlangıç – bitiş`); yalnız başlangıç varsa
 * "… tarihinden itibaren geçerli"; yalnız bitiş varsa "… tarihine kadar geçerli"; ikisi de yoksa boş
 * (tarihsiz ruhsat normaldir; sigorta/muayenede kırmızı "Bitiş tarihi yok" hapı anlatır). `formatDate`
 * çağıranın kültürünü kapatır (portal başına biçimleyici farklı).
 */
export function documentValidityText(
  doc: Pick<DocumentFacts, 'validFrom' | 'validUntil'>,
  formatDate: (iso: string) => string,
  t: TFunction,
): string {
  const from = doc.validFrom ? formatDate(doc.validFrom) : null;
  const until = doc.validUntil ? formatDate(doc.validUntil) : null;
  if (from && until) {
    return `${from} – ${until}`;
  }
  // #25 finding: a lone start date used to print as a bare date and read like an expiry.
  if (from) {
    return t('documents.validity.fromOnly', { date: from });
  }
  if (until) {
    return t('documents.validity.untilOnly', { date: until });
  }
  return '';
}

/**
 * Aynı türden belgeleri ayırt eden ek: dosya adı, yoksa geçerlilik metni, o da yoksa `undefined`.
 * Erişilebilir adlar ("Ehliyet (x.pdf)") ve silme/onay düğmeleri tek kuraldan beslenir.
 */
export function documentNameDetail(
  doc: { fileName: string | null },
  validityText: string,
): string | undefined {
  return doc.fileName ?? (validityText || undefined);
}

/** Geçerlilik: `Valid` yeşil; `Expiring` gri (+ ikon çağıranda); gerisi kırmızı. */
function validityTone(status: DocumentFacts['validityStatus']): TagColor {
  if (status === 'Valid') {
    return 'green';
  }
  return status === 'Expiring' ? 'gray' : 'red';
}

/** Onay: `Approved` yeşil, `Rejected` kırmızı, bekleyen gri. */
function approvalTone(status: DocumentFacts['approvalStatus']): TagColor {
  if (status === 'Approved') {
    return 'green';
  }
  return status === 'Rejected' ? 'red' : 'gray';
}

export function presentDocument(
  doc: DocumentFacts,
  t: TFunction,
): { quiet: boolean; quietLabel: string; tags: DocumentStatusTag[]; srExtra?: string } {
  const approvalOk = doc.approvalStatus === 'Approved';
  const validityOk = doc.validityStatus === 'Valid';
  const approvalLabel = t(`permits.docApproval.${doc.approvalStatus.toLowerCase()}`);
  // `Missing` araçta yalnız tarihsiz Sigorta/Muayene demektir → "Eksik" yerine "Bitiş tarihi yok".
  const validityLabel =
    doc.validityStatus === 'Missing'
      ? t('vehicles.docStatus.missingEnd')
      : t(`compliance.${doc.validityStatus.toLowerCase()}`);
  const tags: DocumentStatusTag[] = [];
  if (!validityOk) {
    tags.push({
      tone: validityTone(doc.validityStatus),
      label: validityLabel,
      icon: doc.validityStatus === 'Expiring' ? 'hourglass' : undefined,
    });
  }
  if (!approvalOk) {
    tags.push({ tone: approvalTone(doc.approvalStatus), label: approvalLabel });
  }
  const quiet = approvalOk && validityOk;
  return {
    quiet,
    quietLabel: approvalLabel,
    tags,
    srExtra: quiet ? validityLabel : approvalOk ? approvalLabel : undefined,
  };
}
